// components/ScheduleModal.jsx
import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Radio,
  Switch,
  Row,
  Col,
  Space,
  Button,
  Checkbox
} from 'antd';
import {
  BellOutlined,
  CalendarOutlined,
  InfoCircleFilled,
  CheckCircleOutlined,
  PauseCircleOutlined
} from '@ant-design/icons';
import PropTypes from 'prop-types';


import { SCHEDULE_TYPES, DAY_NAMES, 
  getDaysFromRepeatDays,
  getRepeatDaysFromArray  } from '../../Api/scheduleApiService';

const { Option } = Select;

// DayOfWeek enum mapping
const DAY_OF_WEEK = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6
};

// Helper function to convert day names to enum values
const getDayOfWeekValues = (dayNames) => {
  return dayNames.map(dayName => DAY_OF_WEEK[dayName]).filter(val => val !== undefined);
};

// Helper function to convert enum values to day names
const getDayNamesFromValues = (dayValues) => {
  return Object.entries(DAY_OF_WEEK)
    .filter(([_, value]) => dayValues.includes(value))
    .map(([name, _]) => name);
};

const ScheduleModal = ({ 
  visible, 
  onClose, 
  onSubmit, 
  editingSchedule, 
  loading 
}) => {
  const [form] = Form.useForm();
  const [durationType, setDurationType] = useState('endDate');
  const [isEveryday, setIsEveryday] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      if (editingSchedule) {
        // Editing mode
        const hasEndDate = editingSchedule.endDate;
        setDurationType(hasEndDate ? 'endDate' : 'totalDays');

        // Parse reminder times
        let reminderTimes = [];
        if (editingSchedule.reminderTimesJson) {
          try {
            reminderTimes = JSON.parse(editingSchedule.reminderTimesJson);
          } catch (e) {
            console.error('Error parsing reminder times:', e);
            reminderTimes = [];
          }
        } else if (editingSchedule.reminderTimes) {
          reminderTimes = editingSchedule.reminderTimes;
        }

        // Parse reminder days - convert from enum values to day names for display
        let reminderDays = [];
        if (editingSchedule.reminderDays && Array.isArray(editingSchedule.reminderDays)) {
          reminderDays = getDayNamesFromValues(editingSchedule.reminderDays);
          // Check if all days are selected (everyday)
          setIsEveryday(editingSchedule.reminderDays.length === 7);
        } else {
          setIsEveryday(false);
        }

        form.setFieldsValue({
          name: editingSchedule.name,
          type: editingSchedule.type,
          isActive: editingSchedule.isActive,
          repeatDays: getDaysFromRepeatDays(editingSchedule.repeatDays),
          reminderTimes: reminderTimes,
          reminderDays: reminderDays,
          startDate: editingSchedule.startDate ? editingSchedule.startDate.split('T')[0] : null,
          endDate: editingSchedule.endDate ? editingSchedule.endDate.split('T')[0] : null,
        });
      } else {
        // Create mode
        setDurationType('endDate');
        setIsEveryday(false);
        form.resetFields();
        form.setFieldsValue({
          type: 0,
          isActive: true,
          repeatDays: [],
          reminderTimes: [],
          reminderDays: [],
        });
      }
    }
  }, [visible, editingSchedule, form]);

  const handleDurationTypeChange = (e) => {
    setDurationType(e.target.value);
    // Clear the other field when switching
    if (e.target.value === 'endDate') {
      form.setFieldsValue({ totalDays: null });
    } else {
      form.setFieldsValue({ endDate: null });
    }
  };

  // Handle end date input change
  const handleEndDateChange = (e) => {
    if (e.target.value) {
      // Clear total days when user starts typing in end date
      form.setFieldsValue({ totalDays: null });
      setDurationType('endDate');
    }
  };

  // Handle total days input change
  const handleTotalDaysChange = (e) => {
    if (e.target.value) {
      // Clear end date when user starts typing in total days
      form.setFieldsValue({ endDate: null });
      setDurationType('totalDays');
    }
  };

  const formatScheduleData = (values) => {
    const baseData = {
      name: values.name,
      type: values.type,
      isActive: values.isActive !== undefined ? values.isActive : true,
      repeatDays: getRepeatDaysFromArray(values.repeatDays || []),
      reminderTimes: values.reminderTimes || [],
      reminderDays: getDayOfWeekValues(values.reminderDays || []),
      startDate: values.startDate ? new Date(values.startDate).toISOString() : new Date().toISOString(),
      endDate: values.endDate ? new Date(values.endDate).toISOString() : null,
    };

    // If editing, include reminderTimesJson for backend compatibility
    if (editingSchedule) {
      baseData.reminderTimesJson = JSON.stringify(baseData.reminderTimes);
    }

    // If using total days instead of end date, calculate end date
    if (values.totalDays && values.startDate) {
      const startDate = new Date(values.startDate);
      const endDate = new Date(startDate.getTime() + (values.totalDays * 24 * 60 * 60 * 1000));
      baseData.endDate = endDate.toISOString();
    }
    
    return baseData;
  };

  const handleSubmit = async (values) => {
    const scheduleData = formatScheduleData(values);
    await onSubmit(scheduleData);
  };

  const handleEverydayChange = (checked) => {
    setIsEveryday(checked);
    if (checked) {
      // Set all days when everyday is checked
      const allDayNames = Object.keys(DAY_OF_WEEK);
      form.setFieldsValue({ reminderDays: allDayNames });
    } else {
      // Clear selection when everyday is unchecked
      form.setFieldsValue({ reminderDays: [] });
    }
  };

  const generateTimeOptions = () => {
    return Array.from({ length: 24 }, (_, i) => {
      const times = [];
      times.push(`${i.toString().padStart(2, '0')}:00`);
      times.push(`${i.toString().padStart(2, '0')}:30`);
      return times;
    }).flat();
  };

  return (
    <Modal
      title={
        <Space>
          <BellOutlined style={{ color: '#298d9f' }} />
          {editingSchedule ? 'Edit Schedule' : 'Create New Schedule'}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={() => form.submit()}
          style={{ backgroundColor: '#298d9f', borderColor: '#298d9f' }}
        >
          {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
        </Button>
      ]}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ isActive: true }}
        className="mt-4"
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="name"
              label="Schedule Name"
              rules={[{ required: true, message: 'Please enter schedule name' }]}
            >
              <Input placeholder="e.g., Morning Medication" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="type"
              label="Schedule Type"
              rules={[{ required: true, message: 'Please select schedule type' }]}
            >
              <Select placeholder="Select schedule type">
                {Object.entries(SCHEDULE_TYPES).map(([value, label]) => (
                  <Option key={value} value={parseInt(value)}>
                    {label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="startDate"
              label="Start Date"
              rules={[{ required: true, message: 'Please select start date' }]}
            >
              <Input type="date" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Schedule Duration">
          <Radio.Group onChange={handleDurationTypeChange} value={durationType}>
            <Radio.Button value="endDate">
              <CalendarOutlined /> Set End Date
            </Radio.Button>
            <Radio.Button value="totalDays">
              <InfoCircleFilled /> Set Total Days
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        {durationType === 'endDate' ? (
          <Form.Item
            name="endDate"
            label="End Date"
            rules={[{ required: true, message: 'Please select end date' }]}
          >
            <Input 
              type="date" 
              style={{ width: '100%' }} 
              onChange={handleEndDateChange}
            />
          </Form.Item>
        ) : (
          <Form.Item
            name="totalDays"
            label="Total Days"
            rules={[{ required: true, message: 'Please enter total days' }]}
          >
            <Input 
              type="number" 
              placeholder="Enter number of days" 
              min={1}
              onChange={handleTotalDaysChange}
            />
          </Form.Item>
        )}

        <Form.Item name="repeatDays" label="Repeat Days">
          <Select mode="multiple" placeholder="Select days to repeat">
            {DAY_NAMES.map(day => (
              <Option key={day} value={day}>{day}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="reminderTimes" label="Reminder Times">
          <Select
            mode="tags"
            placeholder="Add reminder times (e.g., 08:00, 14:30, 20:00)"
            style={{ width: '100%' }}
          >
            {generateTimeOptions().map(time => (
              <Option key={time} value={time}>
                {time}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label={
          <Space>
            <span>Reminder Days</span>
            <Checkbox 
              checked={isEveryday}
              onChange={(e) => handleEverydayChange(e.target.checked)}
            >
              Everyday
            </Checkbox>
          </Space>
        }>
          <Form.Item name="reminderDays" noStyle>
            <Select 
              mode="multiple" 
              placeholder="Select days for reminders"
              style={{ width: '100%' }}
              disabled={isEveryday}
            >
              {Object.keys(DAY_OF_WEEK).map(dayName => (
                <Option key={dayName} value={dayName}>
                  {dayName}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form.Item>

        <Form.Item name="isActive" valuePropName="checked" label="Active Status">
          <Switch
            checkedChildren={<CheckCircleOutlined />}
            unCheckedChildren={<PauseCircleOutlined />}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

ScheduleModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  editingSchedule: PropTypes.object,
  loading: PropTypes.bool
};

ScheduleModal.defaultProps = {
  editingSchedule: null,
  loading: false
};

export default ScheduleModal;